import React, { memo, useState, Suspense, useRef, useEffect, useCallback, useMemo } from 'react';
import { Handle, Position, useReactFlow, useStore } from 'reactflow';
import CustomResizer from './CustomResizer';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useGeneration } from '../../../context/GenerationContext';
import { Button, message, Spin, Slider, Image, Card, Space, Modal, Tooltip } from 'antd';
import { LoadingOutlined, CameraOutlined, GatewayOutlined, ReloadOutlined, LockOutlined, UnlockOutlined, ExpandAltOutlined, PictureOutlined, PlayCircleFilled, FullscreenOutlined, LogoutOutlined, ScanOutlined } from '@ant-design/icons';
import { Canvas, useLoader, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, useTexture, Line, Text, Html } from '@react-three/drei';
import * as THREE from 'three';
import { useParams } from 'react-router-dom';
import api from '../../../api/client';
import NodeLabel from './NodeLabel';
import { useNodeTitle } from './useNodeTitle';
import { NODE_BG, TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE } from './nodeStyles';

const NEON_PINK = '#ff0055';
const NEON_CYAN = '#00ffff';
const NEON_YELLOW = '#ffcc00';
const NEON_BLUE = '#2de2e6';

// Stable Plane Display
const TexturePlane = ({ url }) => {
    // User requested explicit TextureLoader from THREE
    const texture = useLoader(THREE.TextureLoader, url, (loader) => {
        loader.setCrossOrigin("anonymous");
    });

    return (
        <mesh>
            <planeGeometry args={[4, 4 * (texture.image && texture.image.height ? texture.image.height / texture.image.width : 1)]} />
            <meshBasicMaterial map={texture} side={THREE.DoubleSide} transparent opacity={0.8} />
            {/* Dynamic outline */}
            <lineSegments>
                <edgesGeometry args={[new THREE.PlaneGeometry(4, 4 * (texture.image && texture.image.height ? texture.image.height / texture.image.width : 1))]} />
                <lineBasicMaterial color={NEON_PINK} opacity={0.2} transparent />
            </lineSegments>
        </mesh>
    );
};

// Camera Representation Model
const CameraRep = ({ position, rotation }) => {
    return (
        <group position={position} rotation={rotation}>
            {/* Camera Body */}
            <mesh position={[0, 0, 0.2]}>
                <boxGeometry args={[0.4, 0.3, 0.4]} />
                <meshBasicMaterial color={NEON_YELLOW} />
            </mesh>
            {/* Camera Lens */}
            <mesh position={[0, 0, -0.1]} rotation={[Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.15, 0.2, 0.3, 16]} />
                <meshBasicMaterial color={NEON_YELLOW} />
            </mesh>
            {/* View Cone Hint */}
            <mesh position={[0, 0, -2]} rotation={[Math.PI / 2, 0, 0]}>
                <coneGeometry args={[1.5, 4, 4, 1, true]} />
                <meshBasicMaterial color={NEON_BLUE} transparent opacity={0.1} />
            </mesh>
        </group>
    );
};

// Helper for Angle Labels
const getAzimuthLabel = (deg) => {
    if (deg >= 315 || deg <= 45) return "Front View";
    if (deg > 45 && deg < 135) return "Right Side";
    if (deg >= 135 && deg <= 225) return "Back View";
    if (deg > 225 && deg < 315) return "Left Side";
    return "";
};

const getElevationLabel = (deg) => {
    if (deg >= 80) return "Bird's-eye View";
    if (deg >= 50) return "High-angle";
    if (deg >= 15) return "Elevated";
    if (deg >= -10 && deg < 15) return "Eye-level";
    if (deg < -10) return "Low-angle";
    return "";
};

// Custom Cyberpunk Orbit Visualizer
const OrbitVisualizer = ({ azimuth, elevation, distance }) => {
    const ringRadius = 5;

    // Mapping 'Zoom' (0-10) to Physical Render Distance
    const renderDist = 12 - (distance || 0);

    // Ring Points
    const ringPoints = useMemo(() => {
        const pts = [];
        for (let i = 0; i <= 64; i++) {
            const angle = (i / 64) * Math.PI * 2;
            pts.push(new THREE.Vector3(Math.cos(angle) * ringRadius, 0, Math.sin(angle) * ringRadius));
        }
        return pts;
    }, []);

    // Arc Points
    const arcPoints = useMemo(() => {
        const pts = [];
        for (let i = 0; i <= 32; i++) {
            const angle = (i / 32) * Math.PI; // 0 to PI
            pts.push(new THREE.Vector3(0, Math.cos(angle) * ringRadius, Math.sin(angle) * ringRadius));
        }
        return pts;
    }, []);

    // Converting degrees to radians
    const azRad = (azimuth * Math.PI) / 180;
    const elRad = (elevation * Math.PI) / 180;

    const y = renderDist * Math.sin(elRad);
    const r_xz = renderDist * Math.cos(elRad);
    const x = r_xz * Math.sin(azRad);
    const z = r_xz * Math.cos(azRad);

    const camPos = new THREE.Vector3(x, y, z);

    return (
        <group>
            {/* Horizontal Azimuth Ring (Pink) */}
            <Line points={ringPoints} color={NEON_PINK} lineWidth={2} opacity={0.3} transparent />

            {/* Azimuth Indicator */}
            <group rotation={[0, azRad, 0]}>
                <mesh position={[0, 0, ringRadius]}>
                    <sphereGeometry args={[0.2, 16, 16]} />
                    <meshBasicMaterial color={NEON_PINK} />
                </mesh>

                {/* Vertical Elevation Arc (Cyan) */}
                <group rotation={[0, -Math.PI / 2, 0]}>
                    <Line points={arcPoints} color={NEON_CYAN} lineWidth={2} opacity={0.5} transparent />
                    <mesh position={[0, Math.sin(elRad) * ringRadius, Math.cos(elRad) * ringRadius]}>
                        <sphereGeometry args={[0.2, 16, 16]} />
                        <meshBasicMaterial color={NEON_CYAN} />
                    </mesh>
                </group>
            </group>

            {/* Virtual Camera Model */}
            <CameraRep position={camPos} rotation={[0, Math.atan2(x, z), 0]} />
            {/* Shot Line (Blue) */}
            <Line points={[new THREE.Vector3(0, 0, 0), camPos]} color={NEON_BLUE} lineWidth={2} opacity={0.8} transparent />

            {/* Grid Floor */}
            <gridHelper args={[20, 20, 0x333333, 0x111111]} position={[0, 0, 0]} />
            <axesHelper args={[2]} />
        </group>
    );
};


const QwenNode = ({ id, data, selected }) => {
    const { projectId: canvasId } = useParams();
    const { getNodes, setNodes, getEdges } = useReactFlow();
    const [generating, setGenerating] = useState(false);
    const [hovered, setHovered] = useState(false);
    const onTitleChange = useNodeTitle(id, data);
    const { trackGeneration } = useGeneration();

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    // Optimized selector to get connected image without subscribing to all node changes
    const connectedSrc = useStore(
        useCallback((s) => {
            const edge = s.edges.find((e) => e.target === id && e.targetHandle === 'left-input');
            if (!edge) return null;
            // nodeInternals is a Map in React Flow v11+
            return s.nodeInternals.get(edge.source)?.data?.src;
        }, [id])
    );

    // Determine current input image (connected source takes precedence over manual/stored)
    const currentInput = connectedSrc || data.inputImage || null;

    const [cameraParams, setCameraParams] = useState({ azimuth: 0, elevation: 0, distance: 5 });
    const [interactive, setInteractive] = useState(true);

    // New State for Preview (not strictly needed as data.generatedImages is used for list, but kept if needed)
    // const [selectedImage, setSelectedImage] = useState(null); // Unused in original code effectively

    // Use Server Proxy for Visualization to bypass CORS
    // Use Server Proxy for Visualization to bypass CORS
    const { currentUser } = useAuth();
    const [token, setToken] = useState(null);

    useEffect(() => {
        if (currentUser) {
            currentUser.getIdToken().then(setToken);
        }
    }, [currentUser]);

    const visualizationUrl = useMemo(() => {
        if (!currentInput || !token) return null;
        const baseUrl = api.defaults.baseURL || 'http://localhost:3002';
        return `${baseUrl}/storage/proxy?url=${encodeURIComponent(currentInput)}&token=${token}`;
    }, [currentInput, token]);

    const updateData = useCallback((key, value) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return { ...node, data: { ...node.data, [key]: value } };
            }
            return node;
        }));
        if (data.onSave) data.onSave(id, { [key]: value });
    }, [id, setNodes, data]);

    const handleParamChange = (key, value) => {
        setCameraParams(prev => ({ ...prev, [key]: value }));
    };

    const paramsRef = useRef(cameraParams);
    useEffect(() => { paramsRef.current = cameraParams; }, [cameraParams]);

    const handleGenerate = async () => {
        if (!currentInput) {
            message.warning('Please connect an image source.');
            return;
        }

        setGenerating(true);
        try {
            const { azimuth, elevation, distance } = paramsRef.current;

            const response = await api.post('/fal/qwen-image-edit-2511-multiple-angles', {
                image_urls: [currentInput],
                horizontal_angle: azimuth,
                vertical_angle: elevation,
                zoom: distance,
                canvas_id: canvasId,
                node_id: id
            });

            const { request_id } = response.data;
            const modelId = 'fal-ai/qwen-image-edit-2511-multiple-angles';

            // setGenerating(false); // REMOVED: Keep glowing until complete/fail

            trackGeneration(request_id, modelId, {
                prompt: 'Qwen 3D View',
                type: 'Image',
                canvas_id: canvasId,
                node_id: id,
                promptProp: 'Qwen 3D View Edit'
            }, {
                onComplete: async (result) => {
                    try {
                        if (result?.images?.length > 0) {
                            const processedImages = [];
                            for (const img of result.images) {
                                let finalUrl = img.url;
                                try {
                                    const upRes = await api.post('/storage/upload-url', { url: finalUrl, bucket: 'media-generations' });
                                    if (upRes.data?.url) finalUrl = upRes.data.url;
                                } catch (e) {
                                    console.error('Upload failed', e);
                                }
                                processedImages.push({ ...img, url: finalUrl });
                            }

                            updateData('generatedImages', processedImages);
                            message.success(`Generated!`);
                        } else {
                            message.error('No images returned.');
                        }
                    } catch (e) {
                        console.error("Error processing result", e);
                    } finally {
                        setGenerating(false); // Stop glow
                    }
                },
                onFail: (err) => {
                    console.error("Generation failed via queue:", err);
                    setGenerating(false); // Stop glow
                }
            });

        } catch (error) {
            console.error('Generation error:', error);
            message.error('Generation failed: ' + (error.response?.data?.error || error.message));
            setGenerating(false); // Stop glow if initial submission fails
        }
    };


    const [isFullscreen, setIsFullscreen] = useState(false);

    // --- Render Logic for 3D Scene ---
    // Moved to function to reuse in normal and fullscreen modes
    const renderScene = (isFull = false) => (
        <div style={{
            width: '100%', height: '100%',
            position: isFull ? 'fixed' : 'relative',
            top: isFull ? 0 : 'auto',
            left: isFull ? 0 : 'auto',
            zIndex: isFull ? 2000 : 'auto',
            background: isFull ? '#000' : 'transparent' // Transparent to show parent BG when not full
        }}>
            {currentInput && visualizationUrl ? (
                <Canvas style={{ width: '100%', height: '100%', display: 'block' }} dpr={[1, 2]}>
                    <PerspectiveCamera makeDefault position={[5, 5, 10]} fov={45} />
                    <ambientLight intensity={0.5} />
                    <pointLight position={[10, 10, 10]} intensity={1} />

                    <Suspense fallback={<Html center><Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: NEON_PINK }} spin />} /></Html>}>
                        <group position={[0, 0, 0]}>
                            <TexturePlane url={visualizationUrl} />
                        </group>
                    </Suspense>

                    <OrbitVisualizer
                        azimuth={cameraParams.azimuth}
                        elevation={cameraParams.elevation}
                        distance={cameraParams.distance}
                    />
                    <OrbitControls
                        makeDefault
                        minDistance={2}
                        maxDistance={50}
                        rotateSpeed={2.5}
                        zoomSpeed={1.2}
                    />
                </Canvas>
            ) : (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', flexDirection: 'column' }}>
                    <CameraOutlined style={{ fontSize: 48, marginBottom: 10, opacity: 0.3 }} />
                    <div style={{ opacity: 0.5 }}>NO SIGNAL</div>
                    <div style={{ fontSize: 12, opacity: 0.3, marginTop: 5 }}>Connect an image to start</div>
                </div>
            )}

            {/* Controls Overlay */}
            <div style={{
                position: 'absolute',
                bottom: isFull ? 40 : 20, // Lifted up in fullscreen
                left: '50%',
                transform: 'translateX(-50%)',
                width: isFull ? '60%' : '90%',
                maxWidth: 600,
                padding: '16px 24px',
                background: 'rgba(22, 27, 34, 0.85)',
                backdropFilter: 'blur(12px)',
                borderRadius: 24,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                zIndex: 10,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                transition: 'all 0.3s ease'
            }}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {/* Sliders Row */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* Azimuth */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 80, fontSize: 10, color: '#8b949e', fontWeight: 'bold' }}>HORIZONTAL</div>
                        <Slider
                            min={0} max={360}
                            value={cameraParams.azimuth}
                            onChange={(v) => handleParamChange('azimuth', v)}
                            style={{ flex: 1, margin: 0 }}
                            trackStyle={{ background: NEON_PINK }}
                            handleStyle={{ borderColor: NEON_PINK, backgroundColor: '#000' }}
                        />
                        <div style={{ width: 100, fontSize: 10, color: NEON_PINK, fontFamily: 'monospace', textAlign: 'right' }}>
                            {getAzimuthLabel(cameraParams.azimuth)} {cameraParams.azimuth}°
                        </div>
                    </div>

                    {/* Elevation */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 80, fontSize: 10, color: '#8b949e', fontWeight: 'bold' }}>VERTICAL</div>
                        <Slider
                            min={-30} max={90}
                            value={cameraParams.elevation}
                            onChange={(v) => handleParamChange('elevation', v)}
                            style={{ flex: 1, margin: 0 }}
                            trackStyle={{ background: NEON_CYAN }}
                            handleStyle={{ borderColor: NEON_CYAN, backgroundColor: '#000' }}
                        />
                        <div style={{ width: 100, fontSize: 10, color: NEON_CYAN, fontFamily: 'monospace', textAlign: 'right' }}>
                            {getElevationLabel(cameraParams.elevation)} {cameraParams.elevation}°
                        </div>
                    </div>

                    {/* Distance */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 80, fontSize: 10, color: '#8b949e', fontWeight: 'bold' }}>ZOOM</div>
                        <Slider
                            min={0} max={10} step={0.1}
                            value={cameraParams.distance}
                            onChange={(v) => handleParamChange('distance', v)}
                            style={{ flex: 1, margin: 0 }}
                            trackStyle={{ background: NEON_YELLOW }}
                            handleStyle={{ borderColor: NEON_YELLOW, backgroundColor: '#000' }}
                        />
                        <div style={{ width: 100, fontSize: 10, color: NEON_YELLOW, fontFamily: 'monospace', textAlign: 'right' }}>
                            {cameraParams.distance}
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', width: '100%' }} />

                {/* Actions Row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Tooltip title={isFull ? "Exit Fullscreen" : "Fullscreen"}>
                        <Button
                            type="text"
                            icon={isFull ? <LogoutOutlined rotate={180} /> : <FullscreenOutlined />}
                            style={{ color: '#fff' }}
                            onClick={() => setIsFullscreen(!isFullscreen)}
                        />
                    </Tooltip>

                    <Button
                        type="primary"
                        shape="round"
                        icon={generating ? <LoadingOutlined /> : <CameraOutlined />}
                        onClick={handleGenerate}
                        disabled={!currentInput || generating}
                        style={{
                            background: generating ? '#333' : NEON_PINK,
                            borderColor: generating ? '#333' : NEON_PINK,
                            boxShadow: generating ? 'none' : `0 0 15px ${NEON_PINK}40`,
                            minWidth: 120
                        }}
                    >
                        {generating ? 'PROCESSING...' : 'CAPTURE'}
                    </Button>
                </div>
            </div>
        </div>
    );


    return (
        <div
            style={{ position: 'relative', height: '100%', width: '100%' }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer isVisible={selected} minWidth={400} minHeight={400} onResizeEnd={(e, p) => data.onResize && data.onResize(id, p)} />

            <NodeLabel
                title={data.nodeTitle || "Qwen 3D Edit"}
                iconType="ant-design"
                icon={<GatewayOutlined />}
                color={NEON_PINK}
                onTitleChange={onTitleChange}
            />

            {/* Left Input Handle */}
            <Handle
                type="target"
                position={Position.Left}
                id="left-input"
                style={getHandleStyle(TYPE_COLORS.image, 'left', handlesVisible)}
            />

            {/* Right Output Handle */}
            <Handle
                type="source"
                position={Position.Right}
                id="right-output"
                style={getHandleStyle(TYPE_COLORS.image, 'right', handlesVisible)}
            />

            {/* Main Content Area */}
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    border: '1px solid #30363d',
                    background: NODE_BG,
                    overflow: 'hidden',
                    borderRadius: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative'
                }}
            >
                <div
                    onMouseEnter={() => setInteractive(true)}
                    onMouseLeave={() => setInteractive(false)}
                    className="nodrag"
                    style={{
                        flex: 1,
                        background: '#0a0a0a',
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        cursor: interactive ? 'grab' : 'default',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {renderScene(false)}
                </div>
            </div>

            {/* Floating Gallery (Right Side) */}
            {data.generatedImages && data.generatedImages.length > 0 && (
                <div className="nodrag" style={{
                    position: 'absolute',
                    top: 300,
                    right: -136, // Push out to right side
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    padding: 4,
                    maxHeight: 'calc(100% - 320px)',
                    overflowY: 'auto',
                    zIndex: 50
                }}>
                    <Image.PreviewGroup>
                        {[...data.generatedImages].reverse().map((img, idx) => (
                            <div
                                key={idx}
                                style={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: 6,
                                    border: '1px solid #30363d',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    background: NODE_BG,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                    position: 'relative',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.borderColor = NEON_PINK}
                                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#30363d'}
                            >
                                <Image
                                    src={img.url}
                                    width={120}
                                    height={120}
                                    style={{ objectFit: 'contain' }}
                                    alt={`gen-${idx}`}
                                />
                            </div>
                        ))}
                    </Image.PreviewGroup>
                </div>
            )}

            {/* Fullscreen Component */}
            <Modal
                title={null}
                footer={null}
                open={isFullscreen}
                onCancel={() => setIsFullscreen(false)}
                width="100vw"
                style={{ top: 0, padding: 0, maxWidth: '100vw', margin: 0 }}
                bodyStyle={{ height: '100vh', padding: 0, overflow: 'hidden', background: '#000', position: 'relative' }}
                closeIcon={null}
                destroyOnClose
            >
                {isFullscreen && renderScene(true)}
            </Modal>
        </div>
    );
};

export default memo(QwenNode);
